import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import styled from 'styled-components';
import { operationType } from '../../../../services/types';
import { validJSON } from '../../../../services/method';
import { downloadBlobFile, DOC_EXT_ICONS } from '../../../../services/fileDownload';

type OutputMediaData = {
   url: string;
   kind: 'audio' | 'image' | 'video' | 'file';
   type?: string;
   name?: string;
   size?: number;
};

const LEGACY_WHATSAPP_STORAGE_BASE = 'http://51.77.194.225:9000/dragify-file-uploader-new';
const CANONICAL_WHATSAPP_STORAGE_BASE = 'https://s3.dragify.ai/dragify-file-uploader-new';

const normalizeOutputMediaUrl = (value: unknown): string => {
   if (typeof value !== 'string') return '';

   const url = value.trim();
   if (url.startsWith(LEGACY_WHATSAPP_STORAGE_BASE)) {
      return `${CANONICAL_WHATSAPP_STORAGE_BASE}${url.slice(LEGACY_WHATSAPP_STORAGE_BASE.length)}`;
   }

   return url;
};

const getMimeBase = (value: unknown): string => String(value || '').split(';')[0].trim().toLowerCase();

const inferMediaKind = (fileData: Record<string, any>): OutputMediaData['kind'] | null => {
   const mimeBase = getMimeBase(fileData.type);
   if (mimeBase.startsWith('audio/')) return 'audio';
   if (mimeBase.startsWith('image/')) return 'image';
   if (mimeBase.startsWith('video/')) return 'video';

   const mediaType = String(fileData.mediaType || '').toLowerCase();
   if (mediaType === 'audio') return 'audio';
   if (mediaType === 'image') return 'image';
   if (mediaType === 'video') return 'video';
   if (mediaType || fileData.hasMedia || fileData.mediaStatus) return 'file';

   return null;
};

const getOutputMediaData = (value: unknown): OutputMediaData | null => {
   if (typeof value === 'string') {
      const directUrl = normalizeOutputMediaUrl(value);
      if (directUrl.startsWith('data:image/')) {
         return {
            url: directUrl,
            kind: 'image',
            name: 'inline-image',
         };
      }
      if (directUrl && /\.(png|jpe?g|gif|webp|svg)(?:[?#].*)?$/i.test(directUrl)) {
         return {
            url: directUrl,
            kind: 'image',
            name: directUrl.split('/').pop(),
         };
      }
      if (directUrl && (directUrl.includes('/tts/') || /\.(wav|mp3|ogg|webm|m4a|aac|flac)(?:[?#].*)?$/i.test(directUrl))) {
         return {
            url: directUrl,
            kind: 'audio',
            name: directUrl.split('/').pop(),
         };
      }
   }

   const parsed = typeof value === 'string' ? validJSON(value) : value;
   if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

   const fileData = parsed as Record<string, any>;
   const url = normalizeOutputMediaUrl(fileData.url || fileData.Location);
   if (!url) return null;

   const kind = inferMediaKind(fileData);
   if (!kind) return null;

   return {
      url,
      kind,
      type: typeof fileData.type === 'string' ? fileData.type : undefined,
      name: typeof fileData.name === 'string' ? fileData.name : url.split('/').pop(),
      size: typeof fileData.size === 'number' ? fileData.size : undefined,
   };
};

const formatBytes = (value?: number): string => {
   if (!value || value < 0) return '';
   if (value < 1024) return `${value} B`;
   if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
   return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const selectInputText = (input: HTMLInputElement | null) => {
   if (!input) return;
   input.focus();
   input.select();
   input.setSelectionRange(0, input.value.length);
};

const fallbackCopyText = (value: string, input?: HTMLInputElement | null): boolean => {
   const textArea = document.createElement('textarea');
   textArea.value = value;
   textArea.setAttribute('readonly', '');
   textArea.style.position = 'fixed';
   textArea.style.top = '-9999px';
   textArea.style.left = '-9999px';
   textArea.style.opacity = '0';
   document.body.appendChild(textArea);
   textArea.focus();
   textArea.select();
   textArea.setSelectionRange(0, value.length);

   let copied = false;
   try {
      copied = document.execCommand('copy');
   } catch {
      copied = false;
   }

   document.body.removeChild(textArea);
   if (!copied) selectInputText(input || null);
   return copied;
};

export function Input({ id, data }: NodeProps<operationType>) {
   const { setNodes } = useReactFlow();
   const textareaRef = React.useRef(null);
   const mediaLinkRef = React.useRef<HTMLInputElement | null>(null);
   const copyResetRef = React.useRef<number | null>(null);
   const [isCopied, setIsCopied] = React.useState(false);

   // Format output value for display
   const displayValue = React.useMemo(() => {
      if (data.action === 'output') {
         if (typeof data.value === 'object' && data.value !== null) {
            return JSON.stringify(data.value, null, 2);
         }
         return data.value;
      }
      return data.value;
   }, [data.value, data.action]);

   // Detect output media payloads (WhatsApp media, TTS audio URLs, file-like media JSON)
   const outputMediaData = React.useMemo(() => {
      if (data.action !== 'output') return null;
      return getOutputMediaData(data.value);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value]);

   // Detect Media Intelligence image_generation output
   const mediaImageData = React.useMemo(() => {
      if (data.action !== 'output') return null;
      try {
         const val = typeof data.value === 'string' ? data.value : JSON.stringify(data.value);
         const parsed = validJSON(val);
         if (parsed?.type === 'image_generation' && parsed?.success && parsed?.image_url) {
            return { url: parsed.image_url, prompt: parsed.prompt || '', model: parsed.metadata?.model || '', size: parsed.metadata?.size || '' };
         }
      } catch { /* not JSON */ }
      return null;
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value]);

   // Detect Document Intelligence document_generation output
   const docFileData = React.useMemo(() => {
      if (data.action !== 'output') return null;
      try {
         const val = typeof data.value === 'string' ? data.value : JSON.stringify(data.value);
         const parsed = validJSON(val);
         if (parsed?.type === 'document_generation' && parsed?.success && parsed?.file_url) {
            const metaParts: string[] = [];
            if (parsed.metadata?.slides != null) metaParts.push(`${parsed.metadata.slides} slides`);
            if (parsed.metadata?.pages  != null) metaParts.push(`${parsed.metadata.pages} pages`);
            if (parsed.metadata?.sheets != null) metaParts.push(`${parsed.metadata.sheets} sheets`);
            return {
               fileUrl:  parsed.file_url as string,
               fileName: (parsed.file_name as string) || `document.${parsed.file_type || 'docx'}`,
               fileType: (parsed.file_type as string) || 'docx',
               icon:     DOC_EXT_ICONS[parsed.file_type] || '📄',
               metaStr:  metaParts.join(' · '),
            };
         }
      } catch { /* not JSON */ }
      return null;
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value]);

   // ---
   React.useLayoutEffect(() => {
      const textarea = textareaRef.current;

      if (!outputMediaData && !mediaImageData && !docFileData && textareaRef.current) { // @ts-ignore
         textarea.style.height = 'auto'; // @ts-ignore
         textarea.style.height = `${textarea.scrollHeight + 4}px`;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value]);

   // ---
   const onChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNodes((nds) =>
         nds.map((node) => {
            if (node.id === id) return { ...node, data: { ...node.data, value: e.target.value } };
            else return node;
         })
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   React.useEffect(() => {
      return () => {
         if (copyResetRef.current) window.clearTimeout(copyResetRef.current);
      };
   }, []);

   const handleCopyMediaLink = React.useCallback(async (url: string) => {
      let copied = false;

      try {
         if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(url);
            copied = true;
         }
      } catch {
         copied = false;
      }

      if (!copied) {
         copied = fallbackCopyText(url, mediaLinkRef.current);
      }

      if (copied) {
         setIsCopied(true);
         if (copyResetRef.current) window.clearTimeout(copyResetRef.current);
         copyResetRef.current = window.setTimeout(() => setIsCopied(false), 1800);
      } else {
         selectInputText(mediaLinkRef.current);
      }
   }, []);

   return (
      <Main className={data.action}>{
         outputMediaData ? (
            <div className={`output-media-container ${outputMediaData.kind}`}>
               {outputMediaData.kind === 'audio' ? (
                  <audio controls preload="metadata" aria-label="Output audio preview" style={{ width: '100%', minWidth: '250px' }}>
                     <source src={outputMediaData.url} type={outputMediaData.type} />
                     Your browser does not support the audio element.
                  </audio>
               ) : outputMediaData.kind === 'image' ? (
                  <div className="output-media-imageSurface">
                     <img
                        src={outputMediaData.url}
                        alt={outputMediaData.name || 'WhatsApp media'}
                        className="output-media-image"
                     />
                  </div>
               ) : outputMediaData.kind === 'video' ? (
                  <video controls preload="metadata" className="output-media-video" aria-label="Output video preview">
                     <source src={outputMediaData.url} type={outputMediaData.type} />
                     Your browser does not support the video element.
                  </video>
               ) : (
                  <div className="output-media-file-preview">
                     <span className="output-media-file-icon">📎</span>
                     <span>Media file</span>
                  </div>
               )}
               <div className="output-media-details">
                  <div className="output-media-header">
                     <span className="output-media-label">
                        {outputMediaData.kind === 'audio'
                           ? 'Audio output'
                           : outputMediaData.kind === 'image'
                              ? 'Image output'
                              : outputMediaData.kind === 'video'
                                 ? 'Video output'
                                 : 'Media output'}
                     </span>
                     {(outputMediaData.name || outputMediaData.type || outputMediaData.size) && (
                        <span className="output-media-meta">
                           {[outputMediaData.name, outputMediaData.type, formatBytes(outputMediaData.size)].filter(Boolean).join(' · ')}
                        </span>
                     )}
                  </div>
                  <div className="output-media-linkBox">
                     <input
                        ref={mediaLinkRef}
                        type="text"
                        readOnly
                        value={outputMediaData.url}
                        className="output-media-linkInput nodrag nowheel"
                        aria-label="Media link"
                        onFocus={(e) => e.target.select()}
                        onClick={(e) => {
                           e.stopPropagation();
                           e.currentTarget.select();
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                     />
                     <div className="output-media-actions">
                        <button
                           type="button"
                           className={`output-media-actionBtn nodrag nowheel ${isCopied ? 'copied' : ''}`}
                           aria-label="Copy media link"
                           onMouseDown={(e) => e.stopPropagation()}
                           onClick={(e) => {
                              e.stopPropagation();
                              handleCopyMediaLink(outputMediaData.url);
                           }}
                        >
                           {isCopied ? 'Copied' : 'Copy'}
                        </button>
                        <a
                           href={outputMediaData.url}
                           target="_blank"
                           rel="noreferrer"
                           className="output-media-openBtn nodrag nowheel"
                           aria-label="Open media link"
                           onMouseDown={(e) => e.stopPropagation()}
                           onClick={(e) => e.stopPropagation()}
                        >
                           Open
                        </a>
                     </div>
                  </div>
                  <span className="output-media-hint">Click the link field or use Copy.</span>
               </div>
            </div>
         ) : docFileData ? (
            // Document Intelligence document_generation output
            <div className="doc-file-container">
               <div className="doc-file-header">
                  <span className="doc-file-icon">{docFileData.icon}</span>
                  <div className="doc-file-info">
                     <span className="doc-file-name">{docFileData.fileName}</span>
                     {docFileData.metaStr && <span className="doc-file-meta">{docFileData.metaStr}</span>}
                  </div>
                  <span className="doc-file-badge">{docFileData.fileType.toUpperCase()}</span>
               </div>
               <button
                  className="doc-download-btn"
                  onClick={() => downloadBlobFile(docFileData.fileUrl, docFileData.fileName)}
               >
                  ↓ Download {docFileData.fileType.toUpperCase()}
               </button>
            </div>
         ) : mediaImageData ? (
            // Media Intelligence image_generation output
            <div className="media-image-container">
               <img src={mediaImageData.url} alt={mediaImageData.prompt || 'Generated image'} className="media-image-preview" />
               <div className="media-image-meta">
                  {mediaImageData.prompt && (
                     <div className="media-image-prompt">
                        <b>Prompt:</b> {mediaImageData.prompt.length > 120 ? mediaImageData.prompt.slice(0, 120) + '…' : mediaImageData.prompt}
                     </div>
                  )}
                  {(mediaImageData.model || mediaImageData.size) && (
                     <div className="media-image-info">
                        {[mediaImageData.model, mediaImageData.size].filter(Boolean).join(' · ')}
                     </div>
                  )}
                  <a
                     href={mediaImageData.url}
                     download="generated-image.png"
                     target="_blank"
                     rel="noreferrer"
                     className="media-download-btn"
                  >
                     ↓ Download Image
                  </a>
               </div>
            </div>
         ) : (
            <textarea
               ref={textareaRef}
               value={displayValue}
               onChange={onChange}
               disabled={data.disabled || data.action === 'output'}
               className={`nodrag nowheel ${data.action === 'input' && !data.value ? 'noValue' : ''}`}
            ></textarea>
         )
      }</Main>
   );
}

// ---
const Main = styled.div`
   min-width: 230px;

   &.output textarea {
      min-width: 250px;
      border: none;
   }
   textarea {
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      background-color: #F6F6F6;
      border: 1px dashed rgb(200, 200, 200);
      padding: 5px 7.5px;
      font-size: 16px;
      border-radius: 10px;
      min-width: 100%;
      max-width: 500px;
      min-height: 60px;
      max-height: 300px;
      resize: both !important;
   }
   .noValue {
      border: 1px solid #F05454;
   }
   .output-media-container {
      display: flex;
      flex-direction: column;
      gap: 10px;
      background: linear-gradient(180deg, #f8fbff 0%, #f3f7fb 100%);
      border-radius: 16px;
      padding: 12px;
      min-width: 250px;
      max-width: 500px;
      border: 1px solid #d9e2ec;
      box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
      
      audio,
      video {
         border-radius: 12px;
         outline: none;
      }
   }
   .output-media-imageSurface {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 220px;
      max-height: 320px;
      padding: 14px;
      border-radius: 12px;
      border: 1px solid #d8e1ea;
      background-color: #eef3f8;
      background-image:
         linear-gradient(45deg, rgba(148, 163, 184, 0.12) 25%, transparent 25%),
         linear-gradient(-45deg, rgba(148, 163, 184, 0.12) 25%, transparent 25%),
         linear-gradient(45deg, transparent 75%, rgba(148, 163, 184, 0.12) 75%),
         linear-gradient(-45deg, transparent 75%, rgba(148, 163, 184, 0.12) 75%);
      background-size: 20px 20px;
      background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
      overflow: hidden;
   }
   .output-media-image {
      width: 100%;
      max-height: 320px;
      object-fit: contain;
      display: block;
      border-radius: 10px;
   }
   .output-media-video {
      width: 100%;
      max-height: 320px;
      background: #000;
   }
   .output-media-file-preview {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 14px;
      border-radius: 12px;
      background: #fff;
      color: #334155;
      font-size: 13px;
      font-weight: 600;
      border: 1px solid #dbe4ee;
   }
   .output-media-file-icon {
      font-size: 18px;
   }
   .output-media-details {
      display: flex;
      flex-direction: column;
      gap: 8px;
      min-width: 0;
   }
   .output-media-header {
      display: flex;
      flex-direction: column;
      gap: 4px;
   }
   .output-media-label {
      font-size: 12px;
      font-weight: 700;
      color: #0f172a;
      text-transform: uppercase;
      letter-spacing: 0.04em;
   }
   .output-media-meta {
      font-size: 11px;
      color: #64748b;
      overflow-wrap: anywhere;
   }
   .output-media-linkBox {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 10px;
      background: #ffffff;
      border: 1px solid #dbe4ee;
      border-radius: 12px;
   }
   .output-media-linkInput {
      width: 100%;
      border: 1px solid #cbd5e1;
      background: #f8fafc;
      border-radius: 10px;
      padding: 10px 12px;
      font-size: 12px;
      line-height: 1.4;
      color: #0f172a;
      outline: none;
      box-sizing: border-box;
   }
   .output-media-linkInput:focus {
      border-color: #38bdf8;
      box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.18);
   }
   .output-media-actions {
      display: flex;
      gap: 8px;
      align-items: center;
      flex-wrap: wrap;
   }
   .output-media-actionBtn,
   .output-media-openBtn {
      border: none;
      border-radius: 10px;
      padding: 8px 12px;
      font-size: 12px;
      font-weight: 700;
      cursor: pointer;
      text-decoration: none;
      transition: transform 0.12s ease, background 0.12s ease, color 0.12s ease;
   }
   .output-media-actionBtn {
      background: #0f172a;
      color: #fff;
   }
   .output-media-actionBtn:hover {
      background: #1e293b;
      transform: translateY(-1px);
   }
   .output-media-actionBtn.copied {
      background: #0f766e;
   }
   .output-media-openBtn {
      background: #e0f2fe;
      color: #0369a1;
      border: 1px solid #bae6fd;
   }
   .output-media-openBtn:hover {
      background: #d0ecfb;
      transform: translateY(-1px);
   }
   .output-media-hint {
      font-size: 11px;
      color: #64748b;
   }
   .media-image-container {
      display: flex;
      flex-direction: column;
      gap: 0;
      border-radius: 10px;
      overflow: hidden;
      border: 1px solid #e0e0e0;
      background: #f6f6f6;
      min-width: 250px;
      max-width: 500px;
   }
   .media-image-preview {
      width: 100%;
      max-height: 320px;
      object-fit: contain;
      display: block;
      background: #f0f0f0;
   }
   .media-image-meta {
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 8px 10px;
      background: #fff;
      border-top: 1px solid #e0e0e0;
   }
   .media-image-prompt {
      font-size: 12px;
      color: #444;
      line-height: 1.4;
   }
   .media-image-info {
      font-size: 11px;
      color: #999;
   }
   .media-download-btn {
      display: inline-block;
      margin-top: 4px;
      padding: 5px 12px;
      background: #007bff;
      color: #fff;
      border-radius: 6px;
      text-decoration: none;
      font-size: 12px;
      font-weight: 600;
      align-self: flex-start;
      &:hover {
         background: #0056b3;
      }
   }
   .doc-file-container {
      display: flex;
      flex-direction: column;
      gap: 0;
      border-radius: 10px;
      overflow: hidden;
      border: 1px solid #bae6fd;
      background: #f0f9ff;
      min-width: 250px;
      max-width: 360px;
   }
   .doc-file-header {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 12px;
      background: #fff;
      border-bottom: 1px solid #bae6fd;
   }
   .doc-file-icon {
      font-size: 24px;
      flex-shrink: 0;
   }
   .doc-file-info {
      display: flex;
      flex-direction: column;
      gap: 2px;
      flex: 1;
      min-width: 0;
   }
   .doc-file-name {
      font-size: 12px;
      font-weight: 600;
      color: #0369a1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
   }
   .doc-file-meta {
      font-size: 10px;
      color: #7dd3fc;
   }
   .doc-file-badge {
      font-size: 10px;
      font-weight: 700;
      color: #0ea5e9;
      background: #e0f2fe;
      border-radius: 4px;
      padding: 2px 6px;
      flex-shrink: 0;
   }
   .doc-download-btn {
      margin: 0;
      padding: 9px 14px;
      background: #0ea5e9;
      color: #fff;
      border: none;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      width: 100%;
      text-align: center;
      transition: background 0.15s;
      &:hover {
         background: #0284c7;
      }
   }
`;
